import React, { useState } from 'react';
import { SearchIcon, FilterIcon, LayoutGridIcon, ListIcon, PlusIcon } from 'lucide-react';
import { LandCard } from '../components/LandCard';
import { Modal } from '../components/Modal';
interface MyLandsPageProps {
  navigate: (page: string, landId?: string) => void;
  t: (key: string) => string;
  lands: any[];
  addLand: (land: any) => void;
}
export function MyLandsPage({
  navigate,
  t,
  lands,
  addLand
}: MyLandsPageProps) {
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  // New land form state
  const [newLand, setNewLand] = useState({
    district: '',
    upazila: '',
    mouza: '',
    khatian: '',
    plot: '',
    type: 'Agricultural',
    area: '',
    ownership: 'Sole'
  });
  const filteredLands = lands.filter(land => land.district.toLowerCase().includes(searchTerm.toLowerCase()) || land.mouza.toLowerCase().includes(searchTerm.toLowerCase()) || land.id.toLowerCase().includes(searchTerm.toLowerCase()));
  const handleAddSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const landEntry = {
      ...newLand,
      id: `LAND-00${lands.length + 1}`,
      mutation: 'Pending',
      tax: 'Due',
      dispute: 'None'
    };
    addLand(landEntry);
    setIsAddModalOpen(false);
    // Reset form
    setNewLand({
      district: '',
      upazila: '',
      mouza: '',
      khatian: '',
      plot: '',
      type: 'Agricultural',
      area: '',
      ownership: 'Sole'
    });
  };
  return <div className="space-y-6 pb-20 lg:pb-6" data-id="element-522">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-523">
        <div data-id="element-524">
          <h2 className="text-2xl font-bold text-slate-800" data-id="element-525">
            {t('mylands.title')}
          </h2>
          <p className="text-slate-500" data-id="element-526">{t('mylands.subtitle')}</p>
        </div>

        <div className="flex flex-wrap items-center gap-3" data-id="element-527">
          <button onClick={() => setIsAddModalOpen(true)} className="bg-[#006A4E] text-white px-4 py-2 rounded-lg font-medium text-sm hover:bg-[#00523c] transition-colors flex items-center gap-2 shadow-sm" data-id="element-528">
            <PlusIcon className="w-4 h-4" data-id="element-529" /> {t('land.addNew')}
          </button>
          <div className="relative flex-1 md:flex-none" data-id="element-530">
            <SearchIcon className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" data-id="element-531" />
            <input type="text" placeholder={t('mylands.searchPlaceholder')} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#006A4E] focus:border-[#006A4E] outline-none w-full md:w-64 text-sm" data-id="element-532" />
          </div>
          <div className="flex border border-slate-200 rounded-lg overflow-hidden ml-auto md:ml-0" data-id="element-533">
            <button onClick={() => setViewMode('grid')} className={`p-2 ${viewMode === 'grid' ? 'bg-slate-100 text-slate-900' : 'bg-white text-slate-500 hover:bg-slate-50'}`} data-id="element-534">
              <LayoutGridIcon className="w-5 h-5" data-id="element-535" />
            </button>
            <button onClick={() => setViewMode('table')} className={`p-2 border-l border-slate-200 ${viewMode === 'table' ? 'bg-slate-100 text-slate-900' : 'bg-white text-slate-500 hover:bg-slate-50'}`} data-id="element-536">
              <ListIcon className="w-5 h-5" data-id="element-537" />
            </button>
          </div>
        </div>
      </div>

      {viewMode === 'grid' ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-538">
          {filteredLands.map(land => <LandCard key={land.id} land={land} onClick={() => navigate('landdetails', land.id)} t={t} data-id="element-539" />)}
        </div> : <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-id="element-540">
          <div className="overflow-x-auto" data-id="element-541">
            <table className="w-full text-left text-sm" data-id="element-542">
              <thead className="bg-slate-50 text-slate-600 font-medium border-b border-slate-200" data-id="element-543">
                <tr data-id="element-544">
                  <th className="py-4 px-6" data-id="element-545">Land ID</th>
                  <th className="py-4 px-6" data-id="element-546">Location</th>
                  <th className="py-4 px-6" data-id="element-547">{t('land.type')}</th>
                  <th className="py-4 px-6" data-id="element-548">{t('land.taxStatus')}</th>
                  <th className="py-4 px-6 text-right" data-id="element-549">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-550">
                {filteredLands.map(land => <tr key={land.id} className="hover:bg-slate-50 transition-colors" data-id="element-551">
                    <td className="py-4 px-6 font-mono font-medium text-slate-800" data-id="element-552">
                      {land.id}
                    </td>
                    <td className="py-4 px-6" data-id="element-553">
                      <p className="font-medium text-slate-800" data-id="element-554">{land.mouza}</p>
                      <p className="text-slate-500 text-xs" data-id="element-555">
                        {land.upazila}, {land.district}
                      </p>
                    </td>
                    <td className="py-4 px-6" data-id="element-556">
                      <p className="text-slate-800" data-id="element-557">
                        {t(`land.${land.type.toLowerCase()}`) !== `land.${land.type.toLowerCase()}` ? t(`land.${land.type.toLowerCase()}`) : land.type}
                      </p>
                      <p className="text-slate-500 text-xs" data-id="element-558">{land.area}</p>
                    </td>
                    <td className="py-4 px-6" data-id="element-559">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${land.tax === 'Paid' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`} data-id="element-560">
                        {t(`status.${land.tax.toLowerCase()}`) !== `status.${land.tax.toLowerCase()}` ? t(`status.${land.tax.toLowerCase()}`) : land.tax}
                      </span>
                    </td>
                    <td className="py-4 px-6 text-right" data-id="element-561">
                      <button onClick={() => navigate('landdetails', land.id)} className="text-[#006A4E] hover:underline font-medium" data-id="element-562">
                        {t('common.viewDetails')}
                      </button>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>}

      {/* Add Land Modal */}
      <Modal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} title={t('mylands.addTitle')} data-id="element-563">
        <form id="add-land-form" onSubmit={handleAddSubmit} className="space-y-4" data-id="element-564">
          <div className="grid grid-cols-2 gap-4" data-id="element-565">
            <div data-id="element-566">
              <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-567">
                {t('land.district')}
              </label>
              <input required type="text" value={newLand.district} onChange={e => setNewLand({
              ...newLand,
              district: e.target.value
            })} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-1 focus:ring-[#006A4E] outline-none" data-id="element-568" />
            </div>
            <div data-id="element-569">
              <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-570">
                {t('land.upazila')}
              </label>
              <input required type="text" value={newLand.upazila} onChange={e => setNewLand({
              ...newLand,
              upazila: e.target.value
            })} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-1 focus:ring-[#006A4E] outline-none" data-id="element-571" />
            </div>
            <div data-id="element-572">
              <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-573">
                {t('land.mouza')}
              </label>
              <input required type="text" value={newLand.mouza} onChange={e => setNewLand({
              ...newLand,
              mouza: e.target.value
            })} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-1 focus:ring-[#006A4E] outline-none" data-id="element-574" />
            </div>
            <div data-id="element-575">
              <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-576">
                {t('land.khatian')}
              </label>
              <input required type="text" value={newLand.khatian} onChange={e => setNewLand({
              ...newLand,
              khatian: e.target.value
            })} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-1 focus:ring-[#006A4E] outline-none" data-id="element-577" />
            </div>
            <div data-id="element-578">
              <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-579">
                {t('land.dag')}
              </label>
              <input required type="text" value={newLand.plot} onChange={e => setNewLand({
              ...newLand,
              plot: e.target.value
            })} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-1 focus:ring-[#006A4E] outline-none" data-id="element-580" />
            </div>
            <div data-id="element-581">
              <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-582">
                {t('land.area')}
              </label>
              <input required type="text" placeholder="e.g. 10 decimals" value={newLand.area} onChange={e => setNewLand({
              ...newLand,
              area: e.target.value
            })} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-1 focus:ring-[#006A4E] outline-none" data-id="element-583" />
            </div>
            <div data-id="element-584">
              <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-585">
                {t('land.type')}
              </label>
              <select value={newLand.type} onChange={e => setNewLand({
              ...newLand,
              type: e.target.value
            })} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-1 focus:ring-[#006A4E] outline-none" data-id="element-586">
                <option value="Agricultural" data-id="element-587">{t('land.agricultural')}</option>
                <option value="Residential" data-id="element-588">{t('land.residential')}</option>
                <option value="Commercial" data-id="element-589">{t('land.commercial')}</option>
              </select>
            </div>
            <div data-id="element-590">
              <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-591">
                {t('land.ownership')}
              </label>
              <select value={newLand.ownership} onChange={e => setNewLand({
              ...newLand,
              ownership: e.target.value
            })} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-1 focus:ring-[#006A4E] outline-none" data-id="element-592">
                <option value="Sole" data-id="element-593">Sole</option>
                <option value="Joint" data-id="element-594">Joint</option>
              </select>
            </div>
          </div>
          <div className="pt-4 flex justify-end gap-2 border-t border-slate-100 mt-6" data-id="element-595">
            <button type="button" onClick={() => setIsAddModalOpen(false)} className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg" data-id="element-596">
              {t('common.cancel')}
            </button>
            <button type="submit" className="px-4 py-2 text-sm font-medium bg-[#006A4E] text-white rounded-lg hover:bg-[#00523c]" data-id="element-597">
              {t('common.save')}
            </button>
          </div>
        </form>
      </Modal>
    </div>;
}